import React, { useCallback, useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { MiddleMileResponse } from "../../app/models/responseModels/middleMileResponse";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";
import {
  MIDDLE_MILE_COLUMN_NAME,
  MIDDLE_MILE_FILTER_COLUMN_OPTIONS,
  MIDDLE_MILE_LABEL_NAME,
} from "./MiddleMileConstants";

const COLUMN_WITH_DATE = [
  "expectedArrival",
  "containerReceived",
  "receivingStarted",
  "receivingEnded",
];

type MiddleMileDataFilterProps = {
  middleMileData: MiddleMileResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<MiddleMileResponse[]>>;
  filteredData: MiddleMileResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const MiddleMileDataFilter = ({
  middleMileData,
  setFilteredData,
  filteredData,
}: MiddleMileDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>("");

  const [clientNameOptions, setClientNameOptions] = useState<string[]>([]); //Dropdown Options
  const [clientNameSelectedOptions, setClientNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [parcelCenterOptions, setParcelCenterOptions] = useState<string[]>([]); //Dropdown Options
  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [externalPartnerRegionOptions, setExternalPartnerRegionOptions] =
    useState<string[]>([]); //Dropdown Options
  const [
    externalPartnerRegionSelectedOptions,
    setExternalPartnerRegionSelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value

  const [trackingTypeOptions, setTrackingTypeOptions] = useState<string[]>([]); //Dropdown Options
  const [trackingTypeSelectedOptions, setTrackingTypeSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [packagesCountOptions, setPackagesCountOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [packagesCountSelectedOptions, setPackagesCountSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [expectedArrivalFromDate, setExpectedArrivalFromDate] =
    useState<Date | null>(null);
  const [expectedArrivalToDate, setExpectedArrivalToDate] =
    useState<Date | null>(null);

  const [containerReceivedFromDate, setContainerReceivedFromDate] =
    useState<Date | null>(null);
  const [containerReceivedToDate, setContainerReceivedToDate] =
    useState<Date | null>(null);

  const [receivingStartedFromDate, setReceivingStartedFromDate] =
    useState<Date | null>(null);
  const [receivingStartedToDate, setReceivingStartedToDate] =
    useState<Date | null>(null);

  const [receivingEndedFromDate, setReceivingEndedFromDate] =
    useState<Date | null>(null);
  const [receivingEndedToDate, setReceivingEndedToDate] = useState<Date | null>(
    null
  );

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
        return clientNameOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
        return externalPartnerRegionOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
        return trackingTypeOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
        return packagesCountOptions;
      }
      return [];
    },
    [
      clientNameOptions,
      parcelCenterOptions,
      externalPartnerRegionOptions,
      trackingTypeOptions,
      packagesCountOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
        return clientNameSelectedOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterSelectedOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
        return externalPartnerRegionSelectedOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
        return trackingTypeSelectedOptions;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
        return packagesCountSelectedOptions;
      }
      return [];
    },
    [
      clientNameSelectedOptions,
      parcelCenterSelectedOptions,
      externalPartnerRegionSelectedOptions,
      trackingTypeSelectedOptions,
      packagesCountSelectedOptions,
    ]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
        return expectedArrivalFromDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
        return containerReceivedFromDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
        return receivingStartedFromDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
        return receivingEndedFromDate;
      }
      return null;
    },
    [
      expectedArrivalFromDate,
      containerReceivedFromDate,
      receivingStartedFromDate,
      receivingEndedFromDate,
    ]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
        return expectedArrivalToDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
        return containerReceivedToDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
        return receivingStartedToDate;
      }
      if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
        return receivingEndedToDate;
      }
      return null;
    },
    [
      expectedArrivalToDate,
      containerReceivedToDate,
      receivingStartedToDate,
      receivingEndedToDate,
    ]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //ClientName dropdown options
    if (selectedColumnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
      setClientNameOptions(
        middleMileData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setClientNameOptions(
        filteredData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Parcel Center dropdown options
    if (
      selectedColumnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
    ) {
      setParcelCenterOptions(
        middleMileData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterOptions(
        filteredData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //External Partner Region dropdown options
    if (
      selectedColumnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION
    ) {
      setExternalPartnerRegionOptions(
        middleMileData
          .map((v) => v.externalPartnerRegion)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setExternalPartnerRegionOptions(
        filteredData
          .map((v) => v.externalPartnerRegion)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Tracking Type dropdown options
    if (selectedColumnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
      setTrackingTypeOptions(
        middleMileData
          .map((v) => v.trackingType)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setTrackingTypeOptions(
        filteredData
          .map((v) => v.trackingType)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Pckages Count dropdown options
    if (selectedColumnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
      setPackagesCountOptions(
        middleMileData
          .map((v) => v.packagesCount)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setPackagesCountOptions(
        filteredData
          .map((v) => v.packagesCount)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, middleMileData]);

  useEffect(() => {
    let newData = [...middleMileData];
    //ClientName Filter
    if (clientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return clientNameSelectedOptions.some((s) => {
          return s === a.clientName;
        });
      });
    }

    //Parcel Center Filter
    if (parcelCenterSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterSelectedOptions.some((s) => {
          return s === a.parcelCenterLocationName;
        });
      });
    }

    //Tracking Filter
    if (trackingNumberFilter) {
      if (trackingNumberFilter.includes(",")) {
        const trackingNumbers = trackingNumberFilter.split(",");
        newData = newData.filter((a) => {
          return trackingNumbers.some((s) => {
            return s === a.trackingNumber;
          });
        });
      } else {
        newData = newData.filter((x) =>
          x.trackingNumber
            .toLowerCase()
            .includes(trackingNumberFilter.toLowerCase())
        );
      }
    }

    //External Partner Region Filter
    if (externalPartnerRegionSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return externalPartnerRegionSelectedOptions.some((s) => {
          return s === a.externalPartnerRegion;
        });
      });
    }

    //Tracking Type Filter
    if (trackingTypeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return trackingTypeSelectedOptions.some((s) => {
          return s === a.trackingType;
        });
      });
    }

    //Packages Count Filter
    if (packagesCountSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return packagesCountSelectedOptions.some((s) => {
          return s === a.packagesCount;
        });
      });
    }

    if (expectedArrivalFromDate && expectedArrivalToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.expectedArrival).toLocaleDateString() >=
            expectedArrivalFromDate.toLocaleDateString() &&
          new Date(x.expectedArrival).toLocaleDateString() <=
            expectedArrivalToDate.toLocaleDateString()
      );
    }

    if (containerReceivedFromDate && containerReceivedToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.containerReceived).toLocaleDateString() >=
            containerReceivedFromDate.toLocaleDateString() &&
          new Date(x.containerReceived).toLocaleDateString() <=
            containerReceivedToDate.toLocaleDateString()
      );
    }
    if (receivingStartedFromDate && receivingStartedToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.receivingStarted).toLocaleDateString() >=
            receivingStartedFromDate.toLocaleDateString() &&
          new Date(x.receivingStarted).toLocaleDateString() <=
            receivingStartedToDate.toLocaleDateString()
      );
    }

    if (receivingEndedFromDate && receivingEndedToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.receivingEnded).toLocaleDateString() >=
            receivingEndedFromDate.toLocaleDateString() &&
          new Date(x.receivingEnded).toLocaleDateString() <=
            receivingEndedToDate.toLocaleDateString()
      );
    }

    setFilteredData(newData);
  }, [
    middleMileData,
    setFilteredData,
    trackingNumberFilter,
    clientNameSelectedOptions,
    parcelCenterSelectedOptions,
    externalPartnerRegionSelectedOptions,
    trackingTypeSelectedOptions,
    packagesCountSelectedOptions,
    expectedArrivalFromDate,
    expectedArrivalToDate,
    containerReceivedFromDate,
    containerReceivedToDate,
    receivingStartedFromDate,
    receivingStartedToDate,
    receivingEndedFromDate,
    receivingEndedToDate,
  ]);

  // ***** Handlers *****
  const handleTrackingNumberChange = (e: any) => {
    let trackingNumbers = e.target.value;
    if (
      trackingNumbers.includes(" ") ||
      trackingNumbers.includes("\t") ||
      trackingNumbers.includes(",")
    ) {
      setTrackingNumberFilter(
        trackingNumbers.split(" ").join(",").split("\t").join(",")
      );
    } else {
      setTrackingNumberFilter(trackingNumbers);
    }
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions([]);
    }
    if (
      removedColumnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
    ) {
      return setParcelCenterSelectedOptions([]);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      return setExternalPartnerRegionSelectedOptions([]);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
      return setTrackingTypeSelectedOptions([]);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
      return setPackagesCountSelectedOptions([]);
    }

    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
      setExpectedArrivalFromDate(null);
      setExpectedArrivalToDate(null);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
      setContainerReceivedFromDate(null);
      setContainerReceivedToDate(null);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
      setReceivingStartedFromDate(null);
      setReceivingStartedToDate(null);
    }
    if (removedColumnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
      setReceivingEndedFromDate(null);
      setReceivingEndedToDate(null);
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
        setClientNameSelectedOptions([]);
      }
      if (
        oldColumnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
      ) {
        setParcelCenterSelectedOptions([]);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
        setExternalPartnerRegionSelectedOptions([]);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
        setTrackingTypeSelectedOptions([]);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
        setPackagesCountSelectedOptions([]);
      }

      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
        setExpectedArrivalFromDate(null);
        setExpectedArrivalToDate(null);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
        setContainerReceivedFromDate(null);
        setContainerReceivedToDate(null);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
        setReceivingStartedFromDate(null);
        setReceivingStartedToDate(null);
      }
      if (oldColumnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
        setReceivingEndedFromDate(null);
        setReceivingEndedToDate(null);
      }
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
      newInputList[index].displayColumnName =
        MIDDLE_MILE_LABEL_NAME.CLIENT_NAME;
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      newInputList[index].displayColumnName =
        MIDDLE_MILE_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME;
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      newInputList[index].displayColumnName =
        MIDDLE_MILE_LABEL_NAME.EXTERNAL_PARTNER_REGION;
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
      newInputList[index].displayColumnName =
        MIDDLE_MILE_LABEL_NAME.TRACKING_TYPE;
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
      newInputList[index].displayColumnName =
        MIDDLE_MILE_LABEL_NAME.PACKAGES_COUNT;
    }

    if (value === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
      newInputList[index].displayColumnName = "Expected Arrival";
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
      newInputList[index].displayColumnName = "Container Received";
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
      newInputList[index].displayColumnName = "Sort Start";
    }
    if (value === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
      newInputList[index].displayColumnName = "Sort End";
    }

    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setTrackingNumberFilter("");
    setClientNameSelectedOptions([]);
    setParcelCenterSelectedOptions([]);
    setExternalPartnerRegionSelectedOptions([]);
    setTrackingTypeSelectedOptions([]);
    setPackagesCountSelectedOptions([]);
    setExpectedArrivalFromDate(null);
    setExpectedArrivalToDate(null);
    setContainerReceivedFromDate(null);
    setContainerReceivedToDate(null);
    setReceivingStartedFromDate(null);
    setReceivingStartedToDate(null);
    setReceivingEndedFromDate(null);
    setReceivingEndedToDate(null);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      return setExternalPartnerRegionSelectedOptions(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE) {
      return setTrackingTypeSelectedOptions(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT) {
      return setPackagesCountSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
      return setExpectedArrivalFromDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
      return setContainerReceivedFromDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
      return setReceivingStartedFromDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
      return setReceivingEndedFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL) {
      return setExpectedArrivalToDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED) {
      return setContainerReceivedToDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED) {
      return setReceivingStartedToDate(value);
    }
    if (columnName === MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED) {
      return setReceivingEndedToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Middle Mile Packages</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {MIDDLE_MILE_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          //minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          //minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <AutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Tracking Number Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"trackingNumber"}
                  margin="normal"
                  id="trackingNumber"
                  label="Tracking Number"
                  name="trackingNumber"
                  size="small"
                  fullWidth
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default MiddleMileDataFilter;
